import { useState } from "react";
import { useSnackbar } from "notistack";

import { Button, Stack, TextField } from "@mui/material";
import { DataGrid, GridColDef, GridToolbarContainer, GridToolbarExport, GridToolbarQuickFilter } from "@mui/x-data-grid";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { PaginationModel } from "../../../../../models/ReportGridModels";

interface QuotesResponseModel {
    data: QuotesData;
    error: IError;
}
interface QuotesData {
    quotes: QuoteModel[];
    hasNextPage: boolean;
}

interface QuoteModel {
    id: string;
    quoteId: string;
    contact: string;
    quoteNumber: string;
    reference: string;
    status: string;
    title: string;
    summary: string;
    date: string;
    expiryDate: string;
    subTotal: string;
    totalTax: string;
    total: string;
}

interface QuoteFilterRequestModel {
    clientName: string;
    quoteNumber: string;
    referenceName: string;
}

function CustomToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarExport />
            <GridToolbarQuickFilter sx={{ marginLeft: "10px" }} />
        </GridToolbarContainer>
    );
}

const columnVisibility = {
    id: false,
}

function QuotesViewer() {
    const { enqueueSnackbar } = useSnackbar();
    const [paginationModel, setPaginationModel] = useState<PaginationModel>({
        pageSize: 100,
        page: 0,
    });

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 10 },
        { field: 'contact', headerName: 'Contact', width: 200 },
        { field: 'quoteNumber', headerName: 'Q. Number', width: 80 },
        { field: 'reference', headerName: 'Reference', width: 400 },
        { field: 'status', headerName: 'Status', width: 60 },
        { field: 'title', headerName: 'Title', width: 200 },
        { field: 'summary', headerName: 'Summary', width: 100 },
        { field: 'date', headerName: 'Date', width: 107 },
        { field: 'expiryDate', headerName: 'Expiry Date', width: 107 },
        { field: 'subTotal', headerName: 'SubTotal', width: 100 },
        { field: 'totalTax', headerName: 'TotalTax', width: 100 },
        { field: 'total', headerName: 'Total', width: 100 },
        { // download
            field: 'quoteId', headerName: 'Download Quote', width: 160, renderCell: (param: any) => (
                <Button
                    variant="outlined"
                    onClick={() => DownloadPDF(param.row)}>
                    Download PDF
                </Button>
            )
        },
    ]

    const [clientName, setClientName] = useState<string>("");
    const [quoteNumber, setQuoteNumber] = useState<string>("");
    const [reference, setReference] = useState<string>("");

    const [quotes, setQuotes] = useState<QuoteModel[]>([]);

    const GetQuotesStatus = async () => {
        setQuotes([]);

        const reqBody: QuoteFilterRequestModel = {
            clientName: clientName,
            quoteNumber: quoteNumber,
            referenceName: reference
        };

        try {
            var resp: QuotesResponseModel = await api.post("api/Xero/Quotes", {
                json: reqBody,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setQuotes(resp.data.quotes);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const DownloadPDF = async (quote: QuoteModel) => {
        const response = await api.get(`api/Xero/DownloadQuote?quoteId=${quote.quoteId}`, {
            headers: {
                "Authorization": localStorage.getItem("token")!
            }
        }).blob();

        const url = window.URL.createObjectURL(
            new Blob([response]),
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            `${quote.quoteNumber}.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
    }

    const headerHeight: number = 76;
    function GetGridHeight() {
        return window.innerHeight - headerHeight;
    }

    return (
        <Stack direction={"row"}>
            <Stack
                sx={{ width: "250px", marginLeft: "-10px" }}
                alignItems={"center"}>
                {/* ClientName */}
                <Stack
                    alignItems={"center"} >
                    <span>Client name: </span>
                    <TextField
                        value={clientName}
                        onChange={(newValue) => { setClientName(newValue.target.value) }}
                        sx={{ minWidth: "245px" }}
                        size="small" />
                </Stack>
                {/* QuoteNumber */}
                <Stack
                    alignItems={"center"} >
                    <span>Quote number: </span>
                    <TextField
                        value={quoteNumber}
                        onChange={(newValue) => { setQuoteNumber(newValue.target.value) }}
                        sx={{ minWidth: "245px" }}
                        size="small" />
                </Stack>
                {/* Reference */}
                <Stack
                    alignItems={"center"} >
                    <span>Reference name: </span>
                    <TextField
                        value={reference}
                        onChange={(newValue) => { setReference(newValue.target.value) }}
                        sx={{ minWidth: "245px" }}
                        size="small" />
                </Stack>

                <Button
                    variant="outlined"
                    sx={{ width: "150px", marginTop: "15px" }}
                    onClick={GetQuotesStatus}>
                    Quotes
                </Button>
            </Stack>

            <DataGrid
                sx={{ height: GetGridHeight(), width: "1px", marginTop: "-15px", marginBottom: "-25px", marginLeft: "10px", marginRight: "-20px" }}
                rows={quotes}
                columns={columns}
                columnVisibilityModel={columnVisibility}
                pagination
                pageSizeOptions={[10, 25, 50, 100]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                slots={{ toolbar: CustomToolbar }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 100 },
                    },
                }} />
        </Stack>
    )
}

export default QuotesViewer;